import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import * as Dialog from '@radix-ui/react-dialog';
import CloseSVG from '@birdi/icons/close.svg';
import { closeButtonCSS, contentCSS, portalCSS } from '@birdi/modal-kit';
import { forms } from '@birdi/theme';
import { LocationSelect } from '@birdi/google-places-autocomplete';
import { uploadOne } from '@birdi/uppy-s3';
import { stroot } from '@birdi/js-sdk';
import { mapSitesAPI } from '@birdi/js-sdk/src/public/map-sites';
import type { Location } from '@birdi/types/location';
import { invalidateSites } from './queries';
import { SiteImageUpload } from './SiteImageUpload';
import {
  cancelButtonCSS,
  confirmFooterCSS,
  modalInputCSS,
  modalSeparatorCSS,
  primaryButtonCSS,
  selectTheme,
} from './styles';
import { readAPIErrorMessage } from './utils';

interface BaseModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  site: {
    id: string;
    name: string;
    address?: string | null;
    /** Current uploaded site photo (shown in the change-image modal). */
    s3ImageUrl?: string | null;
  };
}

export const ModalShell = ({
  title,
  children,
  open,
  setOpen,
}: {
  /** Plain text, or a node when the title needs an icon alongside it. */
  title: React.ReactNode;
  children: React.ReactNode;
  open: boolean;
  setOpen: (value: boolean) => void;
}) => (
  <Dialog.Root open={open} onOpenChange={setOpen}>
    <Dialog.Portal>
      <Dialog.Overlay />
      <div css={portalCSS}>
        <Dialog.Content css={contentCSS}>
          <Dialog.Close css={closeButtonCSS}>
            <CloseSVG />
          </Dialog.Close>
          <Dialog.Title>{title}</Dialog.Title>
          <hr css={modalSeparatorCSS} />
          {children}
        </Dialog.Content>
      </div>
    </Dialog.Portal>
  </Dialog.Root>
);

export const RenameSiteModal = ({ open, setOpen, site }: BaseModalProps) => {
  const [name, setName] = useState(site.name);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await mapSitesAPI.updateSite(stroot('naem7o'), site.id, { name });
      invalidateSites(site.id);
      setOpen(false);
    } catch (err) {
      setError(readAPIErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="Rename site" open={open} setOpen={setOpen}>
      <label
        css={css`
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        `}
      >
        Site name
        <input
          type="text"
          value={name}
          data-testid="rename-site-input"
          css={modalInputCSS}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      {error && <div css={forms.errorMessage}>{error}</div>}
      <footer css={confirmFooterCSS}>
        <button
          type="button"
          css={cancelButtonCSS}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          css={primaryButtonCSS}
          disabled={!name.trim() || submitting}
          onClick={submit}
        >
          Save
        </button>
      </footer>
    </ModalShell>
  );
};

export const UpdateAddressModal = ({ open, setOpen, site }: BaseModalProps) => {
  const theme = useTheme();
  const [location, setLocation] = useState<Location | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!location) return;
    setSubmitting(true);
    setError(null);
    try {
      await mapSitesAPI.updateSite(stroot('quu2sh'), site.id, {
        address: location.name,
        center: location.center,
        bbox: location.bbox,
      });
      invalidateSites(site.id);
      setOpen(false);
    } catch (err) {
      setError(readAPIErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="Update address" open={open} setOpen={setOpen}>
      <label
        htmlFor="update-site-address"
        css={css`
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        `}
      >
        Address or coordinates
        <LocationSelect
          inputId="update-site-address"
          defaultValue={site.address || ''}
          placeholder="Search for an address or coordinates"
          theme={selectTheme(theme)}
          // Portal the dropdown to <body> (z-index 9999 > modal's 9000) so the
          // options render above / outside the modal instead of being clipped
          // by its overflow.
          menuPortalTarget={
            typeof window !== 'undefined' ? window.document.body : undefined
          }
          onLocationSelect={(geocode) => setLocation(geocode.location)}
        />
      </label>
      {error && <div css={forms.errorMessage}>{error}</div>}
      <footer css={confirmFooterCSS}>
        <button
          type="button"
          css={cancelButtonCSS}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          css={primaryButtonCSS}
          disabled={!location || submitting}
          onClick={submit}
        >
          Save
        </button>
      </footer>
    </ModalShell>
  );
};

export const ArchiveSiteModal = ({
  open,
  setOpen,
  site,
  onArchived,
}: BaseModalProps & { onArchived?: () => void }) => {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await mapSitesAPI.archiveSite(stroot('too4ki'), site.id);
      invalidateSites(site.id);
      setOpen(false);
      onArchived?.();
    } catch (err) {
      setError(readAPIErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="Archive site" open={open} setOpen={setOpen}>
      <p>
        Are you sure you want to archive <strong>{site.name}</strong>?
      </p>
      <p
        css={(theme) => css`
          color: ${theme.mono70};
        `}
      >
        The site is hidden from your sites list. Its maps are released (not
        deleted) and can be added to another site. Its reference sets are kept.
      </p>
      {error && <div css={forms.errorMessage}>{error}</div>}
      <footer css={confirmFooterCSS}>
        <button
          type="button"
          css={cancelButtonCSS}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          css={primaryButtonCSS}
          disabled={submitting}
          data-testid="confirm-delete-site"
          onClick={submit}
        >
          Archive site
        </button>
      </footer>
    </ModalShell>
  );
};

export const ChangeSiteImageModal = ({
  open,
  setOpen,
  site,
}: BaseModalProps) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [removeCurrent, setRemoveCurrent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setImageFile(null);
      setRemoveCurrent(false);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const hasCurrentImage = !!site.s3ImageUrl;
  // Show the existing photo until the user replaces it or deletes it.
  const shownCurrentImage = removeCurrent ? null : site.s3ImageUrl ?? null;
  const canSave = !!imageFile || (removeCurrent && hasCurrentImage);

  const submit = async () => {
    if (!canSave) return;
    setSubmitting(true);
    setError(null);
    try {
      if (imageFile) {
        const upload = (await Promise.race([
          uploadOne({
            data: new Blob([imageFile], { type: imageFile.type }),
            type: imageFile.type,
            name: imageFile.name,
            public: true,
          }),
          new Promise((_resolve, rejectTimeout) => {
            window.setTimeout(
              () =>
                rejectTimeout(
                  new Error('Image upload timed out — please try again.'),
                ),
              45000,
            );
          }),
        ])) as { uploadURL: string };
        await mapSitesAPI.updateSite(stroot('sim7ta'), site.id, {
          s3ImageUrl: upload.uploadURL,
        });
      } else {
        // Delete the current photo → cover falls back to map media / blank.
        await mapSitesAPI.updateSite(stroot('sim7rm'), site.id, {
          s3ImageUrl: null,
        });
      }
      invalidateSites(site.id);
      setOpen(false);
    } catch (err) {
      setError(readAPIErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="Change site image" open={open} setOpen={setOpen}>
      <span
        css={(theme) => css`
          display: block;
          margin-bottom: 0.75rem;
          font-size: 0.8rem;
          color: ${theme.mono50};
        `}
      >
        Upload a site image. If none is selected it will default to the latest
        orthophoto. Format: JPEG or PNG 00px x 00px
      </span>
      <SiteImageUpload
        imageFile={imageFile}
        onSelect={setImageFile}
        currentImageUrl={shownCurrentImage}
        onRemoveCurrent={() => setRemoveCurrent(true)}
      />
      {error && <div css={forms.errorMessage}>{error}</div>}
      <footer css={confirmFooterCSS}>
        <button
          type="button"
          css={cancelButtonCSS}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          css={primaryButtonCSS}
          disabled={!canSave || submitting}
          data-testid="change-site-image-save"
          onClick={submit}
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </footer>
    </ModalShell>
  );
};
